import React, { PropsWithChildren, useState, useEffect, useRef } from "react";
import { ConnectComponentsProvider } from "@stripe/react-connect-js";
import {
  loadConnectAndInitialize,
  StripeConnectInstance,
} from "@stripe/connect-js";
import { useActiveAgent } from "../contexts/ActiveAgentContext";
import { useAuth } from "@clerk/clerk-react";
import { useSettingsData } from "../contexts/SettingsDataContext";
import { useStripeClientSecret } from "../contexts/StripeClientSecretContext";
import { useFeatureFlags } from "../hooks/useFeatureFlags";

interface StripeProviderProps extends PropsWithChildren {}

export function StripeProvider({ children }: StripeProviderProps) {
  const { activeAgent } = useActiveAgent();
  const { settings, stripeAccountStatus } = useSettingsData();
  const { getToken } = useAuth();
  const { setClientSecret } = useStripeClientSecret();
  const [stripeConnectInstance, setStripeConnectInstance] =
    useState<StripeConnectInstance | null>(null);
  const { isPaymentsFeatureEnabled } = useFeatureFlags();

  useEffect(() => {
    const initializeStripeConnect = async () => {
      if (!activeAgent?.id || stripeAccountStatus === "NONE") {
        return;
      }

      try {
        const fetchClientSecret = async () => {
          const response = await fetch(
            `${process.env.EXPO_PUBLIC_API_BASE}/agents/${activeAgent.id}/stripe/account-session`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${await getToken()}`,
              },
            }
          );

          if (!response.ok) {
            const { error } = await response.json();
            console.error("An error occurred: ", error);
            throw new Error(
              error || "Failed to fetch Payment Account Information"
            );
          } else {
            const { client_secret: clientSecret } = await response.json();
            setClientSecret(clientSecret);
            return clientSecret;
          }
        };

        const instance = await loadConnectAndInitialize({
          publishableKey: process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY!,
          fetchClientSecret: fetchClientSecret,
        });
        setStripeConnectInstance(instance);
      } catch (error) {
        console.error("Error initializing Stripe Connect:", error);
      }
    };

    if (isPaymentsFeatureEnabled()) {
      initializeStripeConnect();
    }
  }, [activeAgent?.id, getToken, settings?.stripeAccountId]);

  // only provide if stripe connect instance is available
  if (stripeConnectInstance) {
    return (
      <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
        {children}
      </ConnectComponentsProvider>
    );
  }
  return <>{children}</>;
}
